import { useState } from 'react';
import { EllipsisVerticalIcon, Reply } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@rocco/components/dropdown-menu';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@rocco/components/tooltip';
import { AlertDialog } from '@rocco/ui/dialog/components/AlertDialog';
import type { Message } from '../../types';

/**
 * The actions for a chat message. Includes:
 * - Reply button to reply to the message.
 * - Dropdown menu for more actions, e.g. forward, delete.
 */
export function ChatMessageActions({
    className,
    message,
    isOutgoing,
    onReply,
    onForward,
    onDelete,
}: {
    className?: string;
    message: Message;
    isOutgoing: boolean;
    onReply?: () => void;
    onForward?: () => void;
    onDelete?: () => void;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);

    // Only show actions for text and file attachment messages.
    const canInteract =
        message.__typename === 'TextMessage' ||
        message.__typename === 'FileAttachmentMessage';

    if (!canInteract) {
        return null;
    }

    return (
        <div
            className={cn(
                'flex shrink-0 items-center gap-[2px]',
                isOutgoing ? 'flex-row-reverse' : 'flex-row',
                className,
                // Keep buttons visible when dropdown is open.
                isOpen && 'opacity-100'
            )}
        >
            <TooltipProvider delayDuration={500}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            className="flex size-[32px] shrink-0 items-center justify-center rounded-full text-black/70 hover:bg-black/10 disabled:pointer-events-none disabled:opacity-50"
                            aria-label="Reply to message"
                            onClick={() => onReply?.()}
                        >
                            <Reply className="size-[16px]" />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent>Reply</TooltipContent>
                </Tooltip>
            </TooltipProvider>
            <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                <DropdownMenuTrigger asChild>
                    <button
                        className="flex size-[32px] shrink-0 items-center justify-center rounded-full text-black/70 hover:bg-black/10 disabled:pointer-events-none disabled:opacity-50"
                        aria-label="More options"
                    >
                        <EllipsisVerticalIcon className="size-[16px]" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-unset min-w-40" align="end">
                    <DropdownMenuItem
                        className="focus:bg-black/10"
                        onClick={() => onForward?.()}
                    >
                        Forward
                    </DropdownMenuItem>
                    {isOutgoing && (
                        <DropdownMenuItem
                            className="focus:bg-black/10"
                            onClick={() => setIsDeleteOpen(true)}
                        >
                            Delete
                        </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
            {isOutgoing && (
                <AlertDialog
                    controlled={{
                        open: isDeleteOpen,
                        setOpen: setIsDeleteOpen,
                    }}
                    title="Delete message?"
                    description="Are you sure you want to delete this message?"
                    onConfirm={() => onDelete?.()}
                />
            )}
        </div>
    );
}
